import { Component, ErrorHandler, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { AirportAutocompleteResult } from '../models/airport-autocomplete-result.model';
import { AirportService } from '../../airport/airport.service';
import { GoogleService } from '../../google/google.service';
import { mergeStates, State } from '../state-view/state.model';
import { SearchQuery } from '../../search/models/search-query.model';
import { isString } from '../../util/helpers';
import { State as USState, STATES } from '../../util/states';
import { Source } from '../models/source.model';
import { AviationEventFilter } from '../models/aviation-event-filter.model';
import { AviationEventFiltersService } from '../services/aviation-event-filters.service';
import { SearchFormType } from '../../search/models/search-form-type.model';
import AutocompletePrediction = google.maps.places.AutocompletePrediction;

export enum AutocompleteType {
  Airports,
  Google,
  Combined
}


export enum AutocompleteOptionType {
  Airport,
  Location,
  Text,
  State
}


export interface AutocompleteOption {
  type: AutocompleteOptionType;
  airport?: AirportAutocompleteResult;
  location?: AutocompletePrediction;
  query?: string;
  state?: USState;
}


/**
 * List of ISO 3166-1 country codes to include in Google autocomplete results. This is limited to 5
 * country codes, and should include countries where we have at least some airport data. Here we include
 * the US, Mexico, and Canada, as well as the US Virgin Islands and Bahamas.
 */
export const COUNTRIES = ['US', 'MX', 'CA', 'VI', 'BS'];


@Component({
  selector: 'app-autocomplete-trigger',
  templateUrl: './autocomplete-trigger.component.html',
  styleUrls: ['./autocomplete-trigger.component.scss']
})
export class AutocompleteTriggerComponent implements OnInit, OnDestroy {
  @ViewChild('auto') auto: MatAutocomplete;
  @Input() searchCtrl: FormControl;
  @Input() query: string;
  @Input() queryType: AutocompleteType = AutocompleteType.Airports;
  @Input() state: State = State.Empty;
  @Input() international = false;
  @Input() currentSearchTab: SearchFormType = SearchFormType.Airports;
  @Output() itemSelected = new EventEmitter<AutocompleteOption>();
  @Output() stateChange = new EventEmitter<State>();

  searchTerm$: Observable<string>;
  airportResults$: Observable<AirportAutocompleteResult[]>;
  googleResults$: Observable<AutocompletePrediction[]>;
  internationalCityResults$: Observable<AutocompletePrediction[]>;
  internationalCountryResults$: Observable<AutocompletePrediction[]>;
  usStateResults$: Observable<USState[]>;
  airportState: State = State.Empty;
  googleState: State = State.Empty;
  aviationEventFilterSubscription: Subscription;
  aviationEventFilter: AviationEventFilter;
  isString = isString;
  searchFormType = SearchFormType;

  get sanitizedQuery(): string {
    return AirportService.sanitizeSearchTerm(this.query);
  }

  constructor(
    private _router: Router,
    private _airportService: AirportService,
    private _googleService: GoogleService,
    private _errorHandler: ErrorHandler,
    private _aviationEventFiltersService: AviationEventFiltersService
  ) {
    this.searchCtrl = new FormControl();
  }

  ngOnInit() {
    this.searchTerm$ = this.searchCtrl.valueChanges.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(300),
      // ignore if next search term is same as previous
      distinctUntilChanged()
    );

    this.aviationEventFilterSubscription = this._aviationEventFiltersService.aviationEventFilter.subscribe(ae => {
      this.aviationEventFilter = ae;
    });

    if (this.queryType === AutocompleteType.Airports || this.queryType === AutocompleteType.Combined) {
      this.airportResults$ = this.searchTerm$.pipe(
        tap(() => {
          this.airportState = State.Loading;
          this._updateState();
        }),
        switchMap(query => this.queryType === AutocompleteType.Combined
          ? this._airportService.getAirportCompletions(query, 5)
          : this._airportService.getAirportCompletions(query)),
        tap(() => {
          this.airportState = State.Content;
          this._updateState();
        }),
        catchError(e => {
          this._errorHandler.handleError(e);
          this.airportState = State.Error;
          this._updateState();
          return of<AirportAutocompleteResult[]>([]);
        })
      );
    }

    if (this.queryType === AutocompleteType.Google || this.queryType === AutocompleteType.Combined) {
      this.googleResults$ = this.searchTerm$.pipe(
        tap(() => {
          this.googleState = State.Loading;
          this._updateState();
        }),
        switchMap(query => this._googleService.getPlacePredictions({
          input: query ? query : ' ',
          types: ['(cities)'],
          componentRestrictions: {
            country: COUNTRIES
          }
        })),
        tap(() => {
          this.googleState = State.Content;
          this._updateState();
        }),
        catchError(e => {
          this._errorHandler.handleError(e);
          this.googleState = State.Error;
          this._updateState();
          return of<AutocompletePrediction[]>([]);
        })
      );
    }

    if (this.queryType === AutocompleteType.Combined) {
      this.usStateResults$ = this.searchTerm$.pipe(
        map(query => {
          if (this.queryType === AutocompleteType.Combined && isString(query)) {
            return this._addUSStates(query);
          }
        }),
        catchError(e => {
          this._errorHandler.handleError(e);
          this.googleState = State.Error;
          this._updateState();
          return of<USState[]>([]);
        })
      );
    }

    if (this.queryType === AutocompleteType.Combined) {
      this.internationalCityResults$ = this.searchTerm$.pipe(
        tap(() => {
          this.googleState = State.Loading;
          this._updateState();
        }),
        switchMap(query => {
          const intResult = this._googleService.getPlacePredictions({
            input: query ? query : ' ',
            types: ['(regions)']
          });
          const intResult2 = intResult.then(intValues => intValues.filter(
            (item) => !item.description.endsWith(', USA')
          ).filter((item2) => item2.types.includes('locality') || item2.types.includes('country')
          ).sort((a, b) => {
            if (a.types.includes('locality') && !b.types.includes('locality')) {
              return -1;
            }
            if (a.types.includes('country') && !b.types.includes('country')) {
              return 1;
            }
            if (a.description < b.description) {
              return -1;
            }
            if (a.description > b.description) {
              return 1;
            }
            return 0;
          }));
          return intResult2;
        }),
        tap(() => {
          this.googleState = State.Content;
          this._updateState();
        }),
        catchError(e => {
          this._errorHandler.handleError(e);
          this.googleState = State.Error;
          this._updateState();
          return of<AutocompletePrediction[]>([]);
        })
      );
    }
  }

  ngOnDestroy() {
    this.aviationEventFilterSubscription.unsubscribe();
  }

  private _addUSStates(query: string): Array<USState> {
    let filteredStates = [];
    if (query.length < 2) {
      return filteredStates;
    }
    if (query.length === 2) {
      filteredStates = STATES.filter(item => item.stateCode.toLowerCase().startsWith(query.toLowerCase()));
    } else {
      filteredStates = STATES.filter(item => item.name.toLowerCase().startsWith(query.toLowerCase()));
    }
    return filteredStates;
  }

  private _updateState() {
    const previousState = this.state;
    this.state = mergeStates(this.airportState, this.googleState);
    if (previousState !== this.state) {
      this.stateChange.emit(this.state);
    }
  }

  displayFunc(value: any): string {
    if (value) {
      if (value.identifier) {
        if (value.icaoId != null && value.icaoId.toUpperCase().startsWith('ZZ')) {
          return `${value.name}`;
        }
        return `${value.icaoId || value.faaId || ''}: ${value.name}`;
      } else if (value.description) {
        return value.description;
      }
    }

    return '';
  }

  getAirportText(result: AirportAutocompleteResult): string {
    if (result.icaoId != null && result.icaoId.toUpperCase().startsWith('ZZ')) {
      return `${result.name}`;
    }
    return `${result.icaoId || result.faaId || ''}: ${result.name}`;
  }

  getCityStateText(result: AirportAutocompleteResult): string {
    const city = result.city;
    const state = result.stateProvince;
    if (city || state) {
      return '(' + [city, state].filter(v => v).join(', ') + ')';
    }
    return '';
  }

  getCityText(): string {
    if (this.queryType === AutocompleteType.Combined) {
      return ' (City)';
    }
    return '';
  }

  getStateTerritoryText(): string {
    return ' (State/Territory)';
  }

  getInternationalText(result: AutocompletePrediction): string {
    if (result.types.includes('country')) {
      return ' (Country)';
    }
    if (result.types.includes('locality')) {
      return ' (City)';
    }
    return '';
  }

  showAiports(): boolean {
     return (this.queryType === AutocompleteType.Airports || this.queryType === AutocompleteType.Combined) && !this.international;
  }

  showGoogle(): boolean {
    return (this.queryType === AutocompleteType.Google || this.queryType === AutocompleteType.Combined) && !this.international;
  }

  showInternational(): boolean {
    return this.queryType === AutocompleteType.Combined && this.international;
  }

  onItemSelected (e: MatAutocompleteSelectedEvent) {
    const value = e.option.value;
    if (value.identifier) {
      this.itemSelected.emit({
        type: AutocompleteOptionType.Airport,
        airport: value,
        query: this.query
      });
    } else if (value.place_id) {
      this.itemSelected.emit({
        type: AutocompleteOptionType.Location,
        location: value,
        query: this.query
      });
    } else if (value.stateCode) {
      this.itemSelected.emit({
        type: AutocompleteOptionType.State,
        state: value,
        query: value.stateCode
      });
    } else if (isString(value) && value.length) {
      this.itemSelected.emit({
        type: AutocompleteOptionType.Text,
        query: value
      });
    }
  }

  /**
   * Enter key handler for the autocomplete control. This will take the user directly to the airport detail
   * page if the search term entered is an exact match on the identifier (either ICAO or FAA). Otherwise, it will
   * navigate the user to the search page with the provided term. Note that this method must be called by the
   * consumer of the autocomplete trigger component.
   * @param query {SearchQuery} Search query model. Only the term needs to be filled in, but all other search
   *    parameters will be serialized if provided and passed to the search page (in case where the search term
   *    is not an exact match on the identifier).
   * @return {Promise<void>}
   */
  async submit(query: SearchQuery) {
    const term = query.sanitizedTerm.toUpperCase();
    if (term) {
      const result = await this._airportService.getAirport(term);
      if (result && query.type !== SearchFormType.AviationEvents) {
        if (result.sourceId === Source.CaribbeanGuide || result.sourceId === Source.BahamasGuide) {
          this._router.navigate(['/airports',  term, 'island']);
        } else {
          this._router.navigate(['/airports', term]);
        }
        return;
      }
    }
    query.aviationEventsFilter = this.aviationEventFilter;
    const params = query.encode();
    this._router.navigate(['/search'], { queryParams: params });
  }
}
